import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { TimeEntry, Project } from '../types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const ContractorTimeEntries: React.FC = () => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [formData, setFormData] = useState({
    projectId: '',
    dateWorked: new Date(),
    hoursWorked: '',
    description: '',
  });
  const [filters, setFilters] = useState({
    projectId: '',
    status: '',
    startDate: '',
    endDate: '',
    search: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 15 seconds to sync:
    // - Project assignments (additions/removals)
    // - Time entry status changes from admin approval
    const interval = setInterval(() => {
      fetchData(true); // Silent refresh (don't show loading spinner)
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const params: any = {};
      if (filters.projectId) params.projectId = filters.projectId;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.search) params.search = filters.search;

      console.log(`[Contractor TimeEntries] 🔍 Fetching data...`);
      const [entriesRes, projectsRes] = await Promise.all([
        api.get('/contractor/time-entries', { params }),
        api.get('/contractor/projects'),
      ]);

      setTimeEntries(entriesRes.data);
      const updatedProjects = projectsRes.data;
      console.log(`[Contractor TimeEntries] ✅ Fetched ${updatedProjects.length} assigned projects:`, updatedProjects.map((p: Project) => p.name));
      console.log(`[Contractor TimeEntries] Project IDs:`, updatedProjects.map((p: Project) => p.id));
      setProjects(updatedProjects);
      
      // If editing and selected project was removed, clear selection
      if (editing && formData.projectId && !updatedProjects.find((p: Project) => p.id === formData.projectId)) {
        console.warn(`[Contractor TimeEntries] Selected project was removed, clearing selection`);
        setFormData({ ...formData, projectId: '' });
        setEditing(null);
      }
      
      // If filter project was removed, clear filter
      if (filters.projectId && !updatedProjects.find((p: Project) => p.id === filters.projectId)) {
        console.warn(`[Contractor TimeEntries] Filter project was removed, clearing filter`);
        setFilters({ ...filters, projectId: '' });
      }
    } catch (error: any) {
      console.error(`[Contractor TimeEntries] ❌ Failed to fetch data:`, error);
      console.error(`[Contractor TimeEntries] Error response:`, error.response?.data);
      console.error(`[Contractor TimeEntries] Error status:`, error.response?.status);
      if (!silent && error.response?.status !== 401) {
        alert(`Failed to load data: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleOpen = (entry?: TimeEntry) => {
    if (entry) {
      setEditing(entry);
      setFormData({
        projectId: entry.projectId,
        dateWorked: new Date(entry.dateWorked),
        hoursWorked: entry.hoursWorked.toString(),
        description: entry.description,
      });
    } else {
      setEditing(null);
      setFormData({
        projectId: projects.length > 0 ? projects[0].id : '', // Pre-select first project if available
        dateWorked: new Date(),
        hoursWorked: '',
        description: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.projectId || !formData.hoursWorked || !formData.description.trim()) {
        alert('Please fill in all required fields');
        return;
      }

      const data = {
        ...formData,
        dateWorked: formData.dateWorked.toISOString(),
        hoursWorked: parseFloat(formData.hoursWorked),
      };

      if (editing) {
        await api.put(`/contractor/time-entries/${editing.id}`, data);
      } else {
        await api.post('/contractor/time-entries', data);
      }

      fetchData();
      handleClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to save time entry';
      alert(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }

    try {
      await api.delete(`/contractor/time-entries/${id}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete time entry');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'default';
      case 'APPROVED':
        return 'info';
      case 'BILLED':
        return 'primary';
      case 'PAID':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">My Time Entries</Typography>
        <Box display="flex" gap={2}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={() => fetchData()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/contractor/time-entries/new')}>
            Log New Hours
          </Button>
        </Box>
      </Box>

      {projects.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>No projects assigned</strong>
          </Typography>
          <Typography variant="body2">
            You don't have any projects assigned yet. Please contact your administrator to assign projects to your account.
            Click "Refresh" to check for newly assigned projects.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={filters.projectId}
              label="Project"
              onChange={(e) => setFilters({ ...filters, projectId: e.target.value })}
            >
              <MenuItem value="">All Projects</MenuItem>
              {projects.length === 0 ? (
                <MenuItem disabled value="no-projects">
                  No projects assigned
                </MenuItem>
              ) : (
                projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="BILLED">Billed</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search descriptions..."
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Project</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Invoice</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeEntries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{format(new Date(entry.dateWorked), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{entry.project?.name || '-'}</TableCell>
                <TableCell align="right">{entry.hoursWorked.toFixed(2)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={entry.status} color={getStatusColor(entry.status) as any} size="small" />
                </TableCell>
                <TableCell>{entry.invoice?.invoiceNumber || '-'}</TableCell>
                <TableCell align="right">
                  {entry.status !== 'BILLED' && entry.status !== 'PAID' && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpen(entry)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(entry.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Time Entry' : 'Log Time Entry'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Project</InputLabel>
              <Select
                value={formData.projectId}
                label="Project"
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                required
                disabled={projects.length === 0}
              >
                {projects.length === 0 ? (
                  <MenuItem disabled value="">
                    No projects assigned. Please contact administrator.
                  </MenuItem>
                ) : (
                  projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {projects.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  You need to be assigned to at least one project to log time entries.
                </Typography>
              )}
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date Worked"
                value={formData.dateWorked}
                onChange={(date) => setFormData({ ...formData, dateWorked: date || new Date() })}
                maxDate={new Date()}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>
            <TextField
              label="Hours Worked"
              type="number"
              value={formData.hoursWorked}
              onChange={(e) => setFormData({ ...formData, hoursWorked: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.25', min: '0', max: '24' }}
              helperText="Enter hours worked (e.g., 2.5 for 2 hours 30 minutes)"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={4}
              helperText={`${formData.description.length} characters`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorTimeEntries;

